import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import Icon from '../../../../atoms/Icon'
import { AgentPanelProvider } from './AgentPanelContext'
import ChatPanel from './chat/ChatPanel'

const useStyles = makeStyles(() => ({
  panel: {
    top: '80px',
    right: 0,
    bottom: 0,
    height: 'auto',
    background: 'white',
    zIndex: 1000,
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
    opacity: 0,
    '&.slide-in': {
      transform: 'translateX(0)',
      opacity: 1
    }
  },
  resizeStrip: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '10px',
    cursor: 'ew-resize',
    zIndex: 1000,
    userSelect: 'none'
  },
  closeHandle: {
    position: 'absolute',
    top: '50%',
    right: '100%',
    transform: 'translateY(-50%)',
    zIndex: 1001,
    width: '12px',
    height: '56px',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '12px 0 0 12px',
    backgroundColor: 'var(--color-ai-500)',
    outline: 'var(--border-primary-thin)',
    color: 'var(--color-contrast-ai-500)',
    stroke: 'var(--color-contrast-ai-500)',
    cursor: 'pointer',
    transition: 'background-color 200ms ease-in-out, color 200ms ease-in-out',
    '&:hover': {
      backgroundColor: 'var(--color-ai-600)'
    }
  }
}))

const MIN_WIDTH = 400
const MAX_ABS_WIDTH = 800
const computeMaxWidth = () => Math.min(MAX_ABS_WIDTH, Math.round(window.innerWidth * 0.33))
const clampWidth = (w) => Math.max(MIN_WIDTH, Math.min(w, computeMaxWidth()))

/**
 * Slide-in side panel for AI agent interactions.
 * Wraps content in AgentPanelProvider so child components
 * can access agent state via useAgentPanelContext.
 *
 * @param {string} agentId - Which agent to use (e.g. 'ae-view-builder', 'ae-view-editor')
 * @param {Function} onClose - Called when the panel is dismissed
 * @param {Function} [onJsxUpdate] - Called when user applies generated JSX
 * @param {Function} [onAttachmentLoadStart] - Called when an attachment starts loading
 * @param {Function} [onVersionCreated] - Called when a new version is created
 * @param {Function} [onThreadIdChange] - Called when a new thread is created
 * @param {string} [initialThreadId] - Resume an existing thread
 * @param {Object} [viewContext] - View context for agents that need it ({ viewId, content })
 */
const AgentSidePanel = forwardRef(({
  agentId,
  onClose,
  onJsxUpdate,
  onAttachmentLoadStart,
  onThreadIdChange,
  onVersionCreated,
  initialThreadId,
  viewContext,
  width,
  onWidthChange,
  onResizeStateChange
}, ref) => {
  const classes = useStyles()
  const containerRef = useRef(null)
  const [isSlideIn, setIsSlideIn] = useState(false)

  useLayoutEffect(() => {
    const timer = setTimeout(() => setIsSlideIn(true), 50)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const onWheel = e => { if (e.deltaX !== 0) e.preventDefault() }
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [])

  const handleApplyJsx = (jsxContent) => {
    if (onAttachmentLoadStart) onAttachmentLoadStart()
    if (onJsxUpdate) onJsxUpdate(jsxContent)
  }

  const handleClose = () => {
    setIsSlideIn(false)
    setTimeout(() => onClose(), 300)
  }

  useImperativeHandle(ref, () => ({
    close: handleClose
  }))

  const handleResizeStart = (e) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = width

    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
    onResizeStateChange?.(true)

    const onMove = (ev) => {
      const delta = startX - ev.clientX
      onWidthChange?.(clampWidth(startWidth + delta))
    }
    const onUp = () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      onResizeStateChange?.(false)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  return (
    <AgentPanelProvider
      agentId={agentId}
      initialThreadId={initialThreadId}
      viewContext={viewContext}
      onApplyJsx={handleApplyJsx}
      onThreadIdChange={onThreadIdChange}
      onVersionCreated={onVersionCreated}
    >
      <div
        ref={containerRef}
        className={`${classes.panel} absolute flex flex-col ${isSlideIn ? 'slide-in' : ''}`}
        style={{ width: `${width}px` }}
      >
        <div className={classes.resizeStrip} onMouseDown={handleResizeStart} />
        <button
          type='button'
          className={`${classes.closeHandle} shadow-lg`}
          onClick={handleClose}
          aria-label='Close'
        >
          <Icon name='chevronRight' customSize={14} />
        </button>
        <ChatPanel />
      </div>
    </AgentPanelProvider>
  )
})

AgentSidePanel.propTypes = {
  agentId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onJsxUpdate: PropTypes.func,
  onAttachmentLoadStart: PropTypes.func,
  onThreadIdChange: PropTypes.func,
  onVersionCreated: PropTypes.func,
  initialThreadId: PropTypes.string,
  viewContext: PropTypes.shape({
    viewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string
  }),
  width: PropTypes.number,
  onWidthChange: PropTypes.func,
  onResizeStateChange: PropTypes.func
}

AgentSidePanel.defaultProps = {
  width: MIN_WIDTH
}

export default AgentSidePanel
